/* ALICIA V2 INTERIOR INTERACTIONS — CONSOLIDATED */
(() => {
  const root = document.querySelector('#ag-work');
  if (!root) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const hero = root.querySelector('.agw-hero');
  const cards = Array.from(root.querySelectorAll('.agw-card'));
  const path = root.querySelector('.agw-path');
  const choices = Array.from(root.querySelectorAll('.agw-choice'));
  const note = root.querySelector('.agw-note');

  const reveals = [hero, ...cards, path, note].filter(Boolean);
  reveals.forEach((el) => el.classList.add('agv2-reveal'));

  if (reduce.matches || !('IntersectionObserver' in window)) {
    reveals.forEach((el) => el.classList.add('is-visible'));
  } else {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: .10 });
    reveals.forEach((el) => observer.observe(el));
  }

  const clamp = (v,min,max) => Math.min(max,Math.max(min,v));
  let stableWidth = window.innerWidth;
  let stableHeight = document.documentElement.clientHeight || window.innerHeight;
  let frame = null;

  const resetCard = (card) => {
    card.style.setProperty('--agv2-card-x','0px');
    card.style.setProperty('--agv2-card-y','0px');
    card.style.setProperty('--agv2-card-r','0deg');
    card.style.setProperty('--agv2-card-ry','0deg');
    card.style.setProperty('--agv2-card-s','1');
    card.style.setProperty('--agv2-card-shadow-y','14px');
    card.style.setProperty('--agv2-card-shadow-blur','36px');
    card.style.setProperty('--agv2-card-shadow-alpha','.07');
  };

  const bindDesktopPointer = () => {
    if (reduce.matches || !fine.matches) return;
    cards.forEach((card) => {
      if (card.dataset.agv2Pointer === 'true') return;
      card.dataset.agv2Pointer = 'true';
      card.addEventListener('pointermove',(event) => {
        const rect = card.getBoundingClientRect();
        const px = clamp((event.clientX - rect.left) / rect.width,0,1);
        const py = clamp((event.clientY - rect.top) / rect.height,0,1);
        card.style.setProperty('--agv2-card-x', `${((px - .5) * 8).toFixed(1)}px`);
        card.style.setProperty('--agv2-card-y', `${((py - .5) * 4).toFixed(1)}px`);
        card.style.setProperty('--agv2-card-r', `${((.5 - py) * .8).toFixed(2)}deg`);
        card.style.setProperty('--agv2-card-ry', `${((px - .5) * 4.2).toFixed(2)}deg`);
        card.style.setProperty('--agv2-card-s','1.006');
      },{passive:true});
      card.addEventListener('pointerleave',() => resetCard(card));
    });
  };
  bindDesktopPointer();

  const update = () => {
    if (reduce.matches) {
      frame = null;
      return;
    }

    const vh = fine.matches ? (window.innerHeight || stableHeight) : stableHeight;
    const center = vh * .54;

    if (hero) {
      const rect = hero.getBoundingClientRect();
      const p = clamp((vh - rect.top) / (vh + rect.height),0,1);
      hero.style.setProperty('--agv2-hero-y', `${((.5 - p) * (fine.matches ? 6 : 14)).toFixed(1)}px`);
      hero.style.setProperty('--agv2-hero-orb-x', `${((p - .5) * (fine.matches ? 48 : 88)).toFixed(1)}px`);
      hero.style.setProperty('--agv2-hero-orb-y', `${((p - .5) * (fine.matches ? 74 : 118)).toFixed(1)}px`);
      hero.style.setProperty('--agv2-hero-orb-s', `${(.96 + (1 - Math.abs(p - .5) * 2) * .11).toFixed(3)}`);
    }

    if (!fine.matches) {
      cards.forEach((card,i) => {
        const rect = card.getBoundingClientRect();
        if (rect.bottom < -160 || rect.top > vh + 160) return;
        const delta = clamp(((rect.top + rect.height / 2) - center) / (vh * .64),-1,1);
        const focus = 1 - Math.abs(delta);
        const dir = i % 2 === 0 ? -1 : 1;

        card.style.setProperty('--agv2-card-x', `${(dir * delta * 16).toFixed(1)}px`);
        card.style.setProperty('--agv2-card-y', `${(delta * 17).toFixed(1)}px`);
        card.style.setProperty('--agv2-card-r', `${(dir * delta * 1.05).toFixed(2)}deg`);
        card.style.setProperty('--agv2-card-ry', `${(dir * delta * 2.35).toFixed(2)}deg`);
        card.style.setProperty('--agv2-card-s', `${(.988 + focus * .020).toFixed(3)}`);
        card.style.setProperty('--agv2-card-shadow-y', `${(16 + focus * 13).toFixed(1)}px`);
        card.style.setProperty('--agv2-card-shadow-blur', `${(40 + focus * 22).toFixed(1)}px`);
        card.style.setProperty('--agv2-card-shadow-alpha', `${(.075 + focus * .070).toFixed(3)}`);
      });
    }

    if (path) {
      const rect = path.getBoundingClientRect();
      const p = clamp((vh - rect.top) / (vh + rect.height),0,1);
      path.style.setProperty('--agv2-path-y', `${((.5 - p) * (fine.matches ? 5 : 12)).toFixed(1)}px`);
      path.style.setProperty('--agv2-path-glow-x', `${((p - .5) * (fine.matches ? 52 : 90)).toFixed(1)}px`);
      path.style.setProperty('--agv2-path-glow-y', `${((p - .5) * (fine.matches ? 82 : 130)).toFixed(1)}px`);

      if (!fine.matches) {
        choices.forEach((choice,i) => {
          const cr = choice.getBoundingClientRect();
          const delta = clamp(((cr.top + cr.height / 2) - center) / (vh * .70),-1,1);
          const focus = 1 - Math.abs(delta);
          const dir = i % 2 === 0 ? -1 : 1;
          choice.style.setProperty('--agv2-choice-x', `${(dir * delta * 10).toFixed(1)}px`);
          choice.style.setProperty('--agv2-choice-y', `${(delta * 8).toFixed(1)}px`);
          choice.style.setProperty('--agv2-choice-s', `${(.992 + focus * .014).toFixed(3)}`);
          choice.style.boxShadow = `0 ${(10 + focus * 8).toFixed(1)}px ${(28 + focus * 14).toFixed(1)}px rgba(36,13,43,${(.12 + focus * .07).toFixed(3)})`;
        });
      }
    }

    if (note) {
      const rect = note.getBoundingClientRect();
      const delta = clamp(((rect.top + rect.height / 2) - center) / (vh * .70),-1,1);
      note.style.setProperty('--agv2-note-x', `${(delta * (fine.matches ? 5 : 9)).toFixed(1)}px`);
      note.style.setProperty('--agv2-note-y', `${(delta * (fine.matches ? 7 : 12)).toFixed(1)}px`);
      note.style.setProperty('--agv2-note-glow-x', `${(-delta * (fine.matches ? 40 : 74)).toFixed(1)}px`);
    }

    frame = null;
  };

  const requestUpdate = () => {
    if (frame === null) frame = requestAnimationFrame(update);
  };

  window.addEventListener('scroll',requestUpdate,{passive:true});
  window.addEventListener('resize',() => {
    const nextWidth = window.innerWidth;
    if (Math.abs(nextWidth - stableWidth) > 40) {
      stableWidth = nextWidth;
      stableHeight = document.documentElement.clientHeight || window.innerHeight;
    }
    requestUpdate();
  },{passive:true});
  window.addEventListener('orientationchange',() => {
    stableWidth = window.innerWidth;
    stableHeight = document.documentElement.clientHeight || window.innerHeight;
    requestUpdate();
  },{passive:true});

  reduce.addEventListener('change',() => {
    if (reduce.matches) {
      cards.forEach(resetCard);
      reveals.forEach((el) => el.classList.add('is-visible'));
    } else {
      bindDesktopPointer();
      requestUpdate();
    }
  });

  update();
})();


/* ALICIA V2 CONTACT PAGE INTERACTIONS */
(() => {
  const root = document.querySelector('#ag-contact');
  if (!root) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const hero = root.querySelector('.ag-contact-hero');
  const cards = Array.from(root.querySelectorAll('.ag-contact-card'));
  const note = root.querySelector('.ag-contact-note');
  const form = document.querySelector('body.page-contact-us .contact form');
  const select = document.querySelector('body.page-contact-us .contact .contact-form__input .select__select');
  const selectWrap = select?.closest('.select');

  const reveals = [hero, ...cards, note, form].filter(Boolean);
  reveals.forEach(el => el.classList.add('agc-reveal'));

  if (reduce.matches || !('IntersectionObserver' in window)) {
    reveals.forEach(el => el.classList.add('is-visible'));
  } else {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold:.10 });
    reveals.forEach(el => observer.observe(el));
  }

  if (select && selectWrap) {
    const syncSelect = () => selectWrap.classList.toggle('has-value', Boolean(select.value));
    select.addEventListener('change',syncSelect);
    syncSelect();
  }

  const clamp = (v,min,max) => Math.min(max,Math.max(min,v));
  let stableWidth = window.innerWidth;
  let stableHeight = document.documentElement.clientHeight || window.innerHeight;
  let frame = null;

  const update = () => {
    if (reduce.matches) {
      frame = null;
      return;
    }

    const vh = fine.matches ? (window.innerHeight || stableHeight) : stableHeight;
    const center = vh * .54;

    if (hero) {
      const rect = hero.getBoundingClientRect();
      const p = clamp((vh - rect.top) / (vh + rect.height),0,1);
      hero.style.setProperty('--agc-hero-y', `${((.5 - p) * (fine.matches ? 5 : 12)).toFixed(1)}px`);
      hero.style.setProperty('--agc-hero-glow-x', `${((p - .5) * (fine.matches ? 44 : 80)).toFixed(1)}px`);
      hero.style.setProperty('--agc-hero-glow-y', `${((p - .5) * (fine.matches ? 64 : 110)).toFixed(1)}px`);
    }

    if (!fine.matches) {
      cards.forEach((card,i) => {
        const rect = card.getBoundingClientRect();
        if (rect.bottom < -140 || rect.top > vh + 140) return;
        const delta = clamp(((rect.top + rect.height / 2) - center) / (vh * .67),-1,1);
        const focus = 1 - Math.abs(delta);
        const dir = i % 2 === 0 ? -1 : 1;
        card.style.setProperty('--agc-card-x', `${(dir * delta * 18).toFixed(1)}px`);
        card.style.setProperty('--agc-card-y', `${(delta * 21).toFixed(1)}px`);
        card.style.setProperty('--agc-card-r', `${(dir * delta * 1.10).toFixed(2)}deg`);
        card.style.setProperty('--agc-card-ry', `${(dir * delta * 2.45).toFixed(2)}deg`);
        card.style.setProperty('--agc-card-s', `${(.985 + focus * .024).toFixed(3)}`);
        card.style.setProperty('--agc-surface-x', `${((.5 - delta) * 74).toFixed(1)}%`);
        card.style.setProperty('--agc-surface-y', `${(28 + focus * 38).toFixed(1)}%`);
        card.style.boxShadow = `0 ${(15 + focus * 14).toFixed(1)}px ${(38 + focus * 24).toFixed(1)}px rgba(80,28,99,${(.075 + focus * .075).toFixed(3)})`;
      });
    }

    if (note) {
      const rect = note.getBoundingClientRect();
      const delta = clamp(((rect.top + rect.height / 2) - center) / (vh * .72),-1,1);
      note.style.setProperty('--agc-note-y', `${(delta * (fine.matches ? 5 : 9)).toFixed(1)}px`);
      note.style.setProperty('--agc-note-glow-x', `${(-delta * (fine.matches ? 36 : 68)).toFixed(1)}px`);
    }

    if (form) {
      const rect = form.getBoundingClientRect();
      const delta = clamp(((rect.top + rect.height / 2) - center) / (vh * 1.2),-1,1);
      form.style.setProperty('--agc-form-y', `${(delta * (fine.matches ? 3 : 7)).toFixed(1)}px`);
    }

    frame = null;
  };

  const requestUpdate = () => {
    if (frame === null) frame = requestAnimationFrame(update);
  };

  window.addEventListener('scroll',requestUpdate,{passive:true});
  window.addEventListener('resize',() => {
    const nextWidth = window.innerWidth;
    if (Math.abs(nextWidth - stableWidth) > 40) {
      stableWidth = nextWidth;
      stableHeight = document.documentElement.clientHeight || window.innerHeight;
    }
    requestUpdate();
  },{passive:true});
  window.addEventListener('orientationchange',() => {
    stableWidth = window.innerWidth;
    stableHeight = document.documentElement.clientHeight || window.innerHeight;
    requestUpdate();
  },{passive:true});

  update();
})();


/* ALICIA V2 CONTACT SURFACE MOTION */
(() => {
  const root = document.querySelector('body.page-contact-us #ag-contact');
  if (!root) return;
  const cards = Array.from(root.querySelectorAll('.ag-contact-card'));
  cards.forEach((card,i) => {
    card.style.setProperty('--agc-surface-phase', `${i * -1.25}s`);
  });
})();


;(()=> {
  const agv2PublishedWorkReady=true;
  const reduceMotion=window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const publishedWorkHref='/pages/alicia-gonzalez#published-work';

  function makeNavItem(className){
    const li=document.createElement('li');
    li.className='agv2-nav-published-work';
    const a=document.createElement('a');
    a.href=publishedWorkHref;
    a.className=className;
    a.innerHTML='<span class="label">PUBLISHED WORK</span>';
    li.appendChild(a);
    return li;
  }

  function injectPublishedWorkNav(){
    document.querySelectorAll('.header__inline-menu .header__menu-item--top .label').forEach(label=>{
      if(label.textContent.trim().toLowerCase()!=='about') return;
      const dropdown=label.closest('dropdown-menu');
      const list=dropdown?.querySelector(':scope > .list-menu-dropdown > ul.list-menu');
      if(!list||list.querySelector('.agv2-nav-published-work')) return;
      const existing=[...list.children].find(li=>li.textContent.trim().toLowerCase().includes('the assignment desk'));
      const item=makeNavItem('header__menu-item list-menu__item focus-inset');
      if(existing) existing.insertAdjacentElement('afterend',item);
      else list.appendChild(item);
    });

    document.querySelectorAll('.menu-drawer__navigation details > summary .menu-drawer__menu-item').forEach(item=>{
      const text=item.textContent.replace(/\s+/g,' ').trim().toLowerCase();
      if(text!=='about') return;
      const details=item.closest('details');
      const list=details?.querySelector(':scope > .menu-drawer__submenu .menu-drawer__inner-submenu > ul.menu-drawer__menu');
      if(!list||list.querySelector('.agv2-nav-published-work')) return;
      const existing=[...list.children].find(li=>li.textContent.replace(/\s+/g,' ').trim().toLowerCase().includes('the assignment desk'));
      const li=document.createElement('li');
      li.className='agv2-nav-published-work';
      const a=document.createElement('a');
      a.href=publishedWorkHref;
      a.className='menu-drawer__menu-item list-menu__item focus-inset';
      a.textContent='PUBLISHED WORK';
      li.appendChild(a);
      if(existing) existing.insertAdjacentElement('afterend',li);
      else list.appendChild(li);
    });
  }

  function injectPublishedWorkHub(){
    if(!document.body.classList.contains('page-alicia-gonzalez')) return;
    const root=document.querySelector('#ag-bio');
    if(!root||root.querySelector('#published-work')) return;

    const hub=document.createElement('section');
    hub.id='published-work';
    hub.className='agv2-published-work';
    hub.setAttribute('aria-labelledby','agv2-pw-title');
    hub.innerHTML=`
      <div class="agv2-pw-head">
        <p class="agv2-pw-kicker">Published Work</p>
        <h2 id="agv2-pw-title">Bylines &amp; Columns</h2>
        <p>Read Alicia beyond The Assignment Desk. Reporting, analysis, essays, and practical business thinking published across major editorial platforms.</p>
      </div>
      <div class="agv2-pw-grid">
        <a class="agv2-pw-card" href="https://www.forbes.com/sites/aliciagonzalez/" target="_blank" rel="noopener">
          <span class="agv2-pw-status">Contributor</span>
          <h3>Forbes</h3>
          <p>Entrepreneurship, small business, marketing, leadership, and the decisions shaping how people build and lead.</p>
          <span class="agv2-pw-go">Read Alicia on Forbes <span aria-hidden="true">↗</span></span>
        </a>
        <a class="agv2-pw-card" href="https://www.businessinsider.com/author/alicia-gonzalez" target="_blank" rel="noopener">
          <span class="agv2-pw-status">Journalism</span>
          <h3>Business Insider</h3>
          <p>First-person essays, reported stories, and as-told-to features about work, life, business, and the choices behind them.</p>
          <span class="agv2-pw-go">Read Alicia on Business Insider <span aria-hidden="true">↗</span></span>
        </a>
        <a class="agv2-pw-card" href="https://www.entrepreneur.com/author/alicia-gonzalez" target="_blank" rel="noopener">
          <span class="agv2-pw-status">Contributor Archive</span>
          <h3>Entrepreneur</h3>
          <p>Marketing, influence, leadership, relationships, and practical ideas for entrepreneurs building an audience and a business.</p>
          <span class="agv2-pw-go">Read Alicia on Entrepreneur <span aria-hidden="true">↗</span></span>
        </a>
        <a class="agv2-pw-card" href="https://ceoworld.biz/author/alicia-gonzalez/" target="_blank" rel="noopener">
          <span class="agv2-pw-status">Column Archive</span>
          <h3>CEOWORLD</h3>
          <p>Leadership, marketing, workplace culture, inclusion, and the systems shaping how organizations communicate and grow.</p>
          <span class="agv2-pw-go">Read Alicia on CEOWORLD <span aria-hidden="true">↗</span></span>
        </a>
        <a class="agv2-pw-card" href="https://www.huffpost.com/author/247modernmom-588" target="_blank" rel="noopener">
          <span class="agv2-pw-status">Archive</span>
          <h3>HuffPost</h3>
          <p>Selected earlier writing from Alicia’s journalism and lifestyle archive.</p>
          <span class="agv2-pw-go">Explore the HuffPost archive <span aria-hidden="true">↗</span></span>
        </a>
        <a class="agv2-pw-card" href="https://muckrack.com/alicia-gonzalez-tv/portfolio" target="_blank" rel="noopener">
          <span class="agv2-pw-status">Portfolio</span>
          <h3>Muck Rack</h3>
          <p>A cross-publication portfolio for browsing additional clips and editorial work in one place.</p>
          <span class="agv2-pw-go">View the journalist portfolio <span aria-hidden="true">↗</span></span>
        </a>
      </div>
      <aside class="agv2-pw-story" aria-labelledby="agv2-pw-story-title">
        <div class="agv2-pw-story__glow" aria-hidden="true"></div>
        <div class="agv2-pw-story__copy">
          <p class="agv2-pw-story__kicker">HAVE A GREAT STORY IDEA?</p>
          <h3 id="agv2-pw-story-title">Pitch Alicia.</h3>
          <p>Have a source, founder, trend, dataset, product, launch, or story angle you think Alicia should know about? Journalism pitches have their own inbox so they do not disappear into a general contact queue.</p>
          <a class="agv2-pw-story__email" href="mailto:alicia.gonzalez.journalist@gmail.com">alicia.gonzalez.journalist@gmail.com</a>
        </div>
        <div class="agv2-pw-story__actions">
          <a class="agv2-pw-story__btn agv2-pw-story__btn--primary" href="/products/pitch-guide-how-to-pitch-alicia-gonzalez-for-forbes-insider-more">Get the free 2026 Pitch Guide <span aria-hidden="true">→</span></a>
          <a class="agv2-pw-story__btn agv2-pw-story__btn--secondary" href="mailto:alicia.gonzalez.journalist@gmail.com">Send a journalism pitch <span aria-hidden="true">↗</span></a>
        </div>
      </aside>
      <div class="agv2-pw-linkedin">
        <div>
          <h3>Keep the professional conversation going.</h3>
          <p>Follow Alicia’s business, marketing, AI, journalism, leadership, and founder work on LinkedIn.</p>
        </div>
        <a href="https://www.linkedin.com/in/iamaliciag/" target="_blank" rel="noopener">Connect on LinkedIn <span aria-hidden="true">↗</span></a>
      </div>
    `;

    const quote=root.querySelector('.ag-bio-quote');
    if(quote) root.insertBefore(hub,quote);
    else root.appendChild(hub);
  }

  function boostContactChoreography(){
    if(reduceMotion||!document.body.classList.contains('page-contact-us')) return;
    const cards=[...document.querySelectorAll('.ag-contact-card')];
    if(!cards.length) return;
    let queued=false;

    const update=()=>{
      queued=false;
      const vh=window.innerHeight||800;
      const mobile=window.matchMedia('(max-width: 749px)').matches;
      cards.forEach((card,index)=>{
        const rect=card.getBoundingClientRect();
        const center=rect.top+(rect.height/2);
        const delta=Math.max(-1,Math.min(1,(center-(vh*.5))/(vh*(mobile?.62:.72))));
        const proximity=1-Math.min(1,Math.abs(delta));
        const direction=index%2===0?-1:1;
        const x=direction*delta*(mobile?16:11);
        const y=Math.abs(delta)*(mobile?12:8)-(proximity*(mobile?3:2));
        const ry=-direction*delta*(mobile?3.1:2.2);
        const scale=(mobile?.958:.968)+(proximity*(mobile?.052:.040));
        const shadowY=14+(proximity*10);
        const shadowBlur=34+(proximity*20);
        const shadowAlpha=.065+(proximity*.055);

        card.style.setProperty('--agc-boost-x',x.toFixed(2)+'px');
        card.style.setProperty('--agc-boost-y',y.toFixed(2)+'px');
        card.style.setProperty('--agc-boost-ry',ry.toFixed(2)+'deg');
        card.style.setProperty('--agc-boost-s',scale.toFixed(4));
        card.style.setProperty('--agc-boost-shadow-y',shadowY.toFixed(1)+'px');
        card.style.setProperty('--agc-boost-shadow-blur',shadowBlur.toFixed(1)+'px');
        card.style.setProperty('--agc-boost-shadow-alpha',shadowAlpha.toFixed(3));
        card.style.zIndex=String(Math.round(proximity*10)+1);
        card.dataset.agv2Focus=proximity>.72?'true':'false';
      });
    };

    const request=()=>{
      if(queued) return;
      queued=true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll',request,{passive:true});
    window.addEventListener('resize',request);
    update();
  }

  injectPublishedWorkNav();
  injectPublishedWorkHub();
  boostContactChoreography();
})();


;(()=> {
  const agv2CoursesReady=true;
  if(!document.body.classList.contains('page-courses')) return;
  const root=document.querySelector('#ag-courses-hub');
  if(!root) return;

  const reduce=window.matchMedia('(prefers-reduced-motion: reduce)');
  if(reduce.matches) return;

  const fine=window.matchMedia('(hover: hover) and (pointer: fine)');
  const hero=root.querySelector('.agch-hero');
  const heroImg=root.querySelector('.agch-hero-banner-img');
  const cards=[...root.querySelectorAll('.agch-card')];
  const begin=root.querySelector('.agch-begin');
  const clamp=(v,min,max)=>Math.min(max,Math.max(min,v));
  let stableHeight=document.documentElement.clientHeight||window.innerHeight||800;
  let frame=null;

  function update(){
    frame=null;
    if(reduce.matches) return;
    const vh=fine.matches?(window.innerHeight||stableHeight):stableHeight;
    const center=vh*.54;

    if(hero){
      const rect=hero.getBoundingClientRect();
      const p=clamp((vh-rect.top)/(vh+rect.height),0,1);
      hero.style.setProperty('--agcourse-hero-y',(((.5-p)*(fine.matches?6:12))).toFixed(1)+'px');
      hero.style.setProperty('--agcourse-hero-s',(1+(1-Math.abs(p-.5)*2)*.008).toFixed(4));
      if(heroImg){
        heroImg.style.setProperty('--agcourse-hero-img-y',(((p-.5)*(fine.matches?12:20))).toFixed(1)+'px');
        heroImg.style.setProperty('--agcourse-hero-img-s',(1.015+(1-Math.abs(p-.5)*2)*.018).toFixed(4));
      }
    }

    cards.forEach((card,index)=>{
      const rect=card.getBoundingClientRect();
      if(rect.bottom<-180||rect.top>vh+180) return;
      const delta=clamp(((rect.top+rect.height/2)-center)/(vh*(fine.matches?.80:.66)),-1,1);
      const focus=1-Math.abs(delta);
      const direction=index%2===0?-1:1;
      const x=direction*delta*(fine.matches?6:17);
      const y=Math.abs(delta)*(fine.matches?5:12)-(focus*(fine.matches?2:4));
      const ry=-direction*delta*(fine.matches?1.6:3.0);
      const scale=(fine.matches?.988:.958)+(focus*(fine.matches?.022:.052));
      const shadowY=17+focus*13;
      const shadowBlur=40+focus*23;
      const shadowAlpha=.075+focus*.070;

      card.style.setProperty('--agcourse-card-x',x.toFixed(2)+'px');
      card.style.setProperty('--agcourse-card-y',y.toFixed(2)+'px');
      card.style.setProperty('--agcourse-card-ry',ry.toFixed(2)+'deg');
      card.style.setProperty('--agcourse-card-s',scale.toFixed(4));
      card.style.setProperty('--agcourse-shadow-y',shadowY.toFixed(1)+'px');
      card.style.setProperty('--agcourse-shadow-blur',shadowBlur.toFixed(1)+'px');
      card.style.setProperty('--agcourse-shadow-alpha',shadowAlpha.toFixed(3));
      card.style.zIndex=String(Math.round(focus*10)+1);

      const img=card.querySelector('.agch-card-img img');
      if(img){
        img.style.setProperty('--agcourse-img-y',(delta*(fine.matches?5:12)).toFixed(1)+'px');
        img.style.setProperty('--agcourse-img-s',(1.035+focus*(fine.matches?.018:.035)).toFixed(4));
      }
    });

    if(begin){
      const rect=begin.getBoundingClientRect();
      const delta=clamp(((rect.top+rect.height/2)-center)/(vh*.75),-1,1);
      begin.style.setProperty('--agcourse-begin-y',(delta*(fine.matches?5:11)).toFixed(1)+'px');
    }
  }

  function request(){
    if(frame!==null) return;
    frame=requestAnimationFrame(update);
  }

  if(fine.matches){
    cards.forEach(card=>{
      card.addEventListener('pointermove',e=>{
        const r=card.getBoundingClientRect();
        const px=(e.clientX-r.left)/r.width;
        const py=(e.clientY-r.top)/r.height;
        card.style.setProperty('--agcourse-card-ry',((px-.5)*3.4).toFixed(2)+'deg');
        card.style.setProperty('--agcourse-card-y',((.5-py)*3.2).toFixed(2)+'px');
        card.style.setProperty('--agcourse-card-s','1.008');
      },{passive:true});
      card.addEventListener('pointerleave',()=>request());
    });
  }

  window.addEventListener('scroll',request,{passive:true});
  window.addEventListener('resize',()=>{
    stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;
    request();
  },{passive:true});
  window.addEventListener('orientationchange',()=>{
    stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;
    request();
  },{passive:true});
  update();
})();


;(()=> {
  const agv2FreeResourcesReady=true;
  const path=window.location.pathname.replace(/\/+$/,'');
  if(path!=='/collections/free-resources') return;

  document.body.classList.add('agv2-free-resources');
  const root=document.querySelector('#MainContent');
  if(!root) return;

  const reduce=window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine=window.matchMedia('(hover: hover) and (pointer: fine)');
  const hero=root.querySelector('.collection-hero');
  const grid=root.querySelector('#product-grid');
  const container=root.querySelector('.product-grid-container');

  root.querySelectorAll('facet-filters-form, facets-drawer, .facets, .facets-container, .facet-filters, .mobile-facets__wrapper, .facets__wrapper, .facets-vertical-sort').forEach(el=>{
    el.classList.add('agv2-resource-hide-controls');
  });

  if(hero&&!hero.querySelector('.agv2-resource-meta')){
    const desc=hero.querySelector('.collection-hero__description');
    const meta=document.createElement('div');
    meta.className='agv2-resource-meta';
    meta.innerHTML='<span>Free resources</span><span>Guides · webinars · worksheets</span><span>Practical by design</span>';
    if(desc) desc.appendChild(meta);
  }

  if(container&&!container.querySelector('.agv2-resource-library-head')){
    const head=document.createElement('div');
    head.className='agv2-resource-library-head';
    head.innerHTML='<p class="agv2-resource-library-head__kicker">BROWSE THE LIBRARY</p><h2>Start with one useful thing.</h2><p>Choose the guide, webinar, or worksheet that solves the problem in front of you right now. Every resource here is free.</p>';
    container.prepend(head);
  }

  const resourceMap={
    'pitch-guide-how-to-pitch-alicia-gonzalez-for-forbes-insider-more':{type:'Pitching & PR'},
    'build-your-ai-staff-free-live-webinar':{type:'AI & Systems'},
    'fix-your-email-before-you-buy-more-traffic-free-email-marketing-field-guide':{type:'Email Marketing'},
    'email-revenue-leak-audit-free-worksheet-template':{type:'Worksheet'}
  };

  const cards=[];
  if(grid){
    [...grid.querySelectorAll('.card-wrapper')].forEach((wrapper,index)=>{
      const link=wrapper.querySelector('a[href*="/products/"]');
      const href=link?.getAttribute('href')||'';
      const match=href.match(/\/products\/([^?/#]+)/);
      const handle=match?.[1]||'';
      const info=resourceMap[handle]||{type:'Free Resource'};
      wrapper.dataset.agv2Resource=handle;
      wrapper.dataset.agv2Index=String(index);

      if(!wrapper.querySelector('.agv2-resource-type')){
        const badge=document.createElement('span');
        badge.className='agv2-resource-type';
        badge.textContent=info.type;
        wrapper.appendChild(badge);
      }

      if(!wrapper.querySelector('.agv2-resource-free-price')){
        const price=wrapper.querySelector('.price');
        const free=document.createElement('span');
        free.className='agv2-resource-free-price';
        free.textContent='FREE';
        if(price) price.insertAdjacentElement('afterend',free);
        else wrapper.querySelector('.card-information__wrapper')?.appendChild(free);
      }

      cards.push(wrapper);
    });
  }

  if(container&&!container.querySelector('.agv2-resource-next')){
    const next=document.createElement('aside');
    next.className='agv2-resource-next';
    next.innerHTML='<div><p class="agv2-resource-next__kicker">WANT TO GO DEEPER?</p><h2>Turn the resource into a system.</h2><p>When you are ready for the full framework, step-by-step implementation, and more support, explore Alicia’s courses.</p></div><a href="/pages/courses">Explore Courses <span aria-hidden="true">→</span></a>';
    const gridContainer=container.querySelector('#ProductGridContainer')||container.querySelector('.collection')||grid;
    if(gridContainer) gridContainer.insertAdjacentElement('afterend',next);
    else container.appendChild(next);
  }

  if(reduce.matches||!cards.length) return;

  const clamp=(v,min,max)=>Math.min(max,Math.max(min,v));
  let stableHeight=document.documentElement.clientHeight||window.innerHeight||800;
  let frame=null;

  function update(){
    frame=null;
    const vh=fine.matches?(window.innerHeight||stableHeight):stableHeight;
    const center=vh*.54;

    cards.forEach((card,index)=>{
      const rect=card.getBoundingClientRect();
      if(rect.bottom<-180||rect.top>vh+180) return;
      const delta=clamp(((rect.top+rect.height/2)-center)/(vh*(fine.matches?.82:.67)),-1,1);
      const focus=1-Math.abs(delta);
      const direction=index%2===0?-1:1;
      const x=direction*delta*(fine.matches?5:15);
      const y=Math.abs(delta)*(fine.matches?4:11)-(focus*(fine.matches?2:4));
      const ry=-direction*delta*(fine.matches?1.4:2.7);
      const scale=(fine.matches?.991:.962)+(focus*(fine.matches?.018:.048));

      card.style.setProperty('--agvr-card-x',x.toFixed(2)+'px');
      card.style.setProperty('--agvr-card-y',y.toFixed(2)+'px');
      card.style.setProperty('--agvr-card-ry',ry.toFixed(2)+'deg');
      card.style.setProperty('--agvr-card-s',scale.toFixed(4));
      card.style.setProperty('--agvr-shadow-y',(17+focus*13).toFixed(1)+'px');
      card.style.setProperty('--agvr-shadow-blur',(41+focus*23).toFixed(1)+'px');
      card.style.setProperty('--agvr-shadow-alpha',(.075+focus*.07).toFixed(3));

      const image=card.querySelector('.card--image-animate');
      if(image){
        image.style.setProperty('--agvr-img-x',(direction*delta*(fine.matches?5:11)).toFixed(1)+'px');
        image.style.setProperty('--agvr-img-y',(delta*(fine.matches?8:16)).toFixed(1)+'px');
        image.style.setProperty('--agvr-img-r',(direction*delta*(fine.matches?.65:1.25)).toFixed(2)+'deg');
        image.style.setProperty('--agvr-img-s',(1.035+focus*(fine.matches?.035:.055)).toFixed(4));
      }
    });
  }

  function request(){
    if(frame!==null) return;
    frame=requestAnimationFrame(update);
  }

  if(fine.matches){
    cards.forEach((card,index)=>{
      const image=card.querySelector('.card--image-animate');
      card.addEventListener('pointermove',e=>{
        const r=card.getBoundingClientRect();
        const px=(e.clientX-r.left)/r.width;
        const py=(e.clientY-r.top)/r.height;
        card.style.setProperty('--agvr-card-ry',((px-.5)*3.2).toFixed(2)+'deg');
        card.style.setProperty('--agvr-card-y',((.5-py)*3.2).toFixed(2)+'px');
        card.style.setProperty('--agvr-card-s','1.006');
        if(image){
          image.style.setProperty('--agvr-img-x',((px-.5)*16).toFixed(1)+'px');
          image.style.setProperty('--agvr-img-y',((py-.5)*15).toFixed(1)+'px');
          image.style.setProperty('--agvr-img-r',((px-.5)*1.6).toFixed(2)+'deg');
          image.style.setProperty('--agvr-img-s','1.075');
        }
      },{passive:true});
      card.addEventListener('pointerleave',request);
    });
  }

  window.addEventListener('scroll',request,{passive:true});
  window.addEventListener('resize',()=>{
    stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;
    request();
  },{passive:true});
  window.addEventListener('orientationchange',()=>{
    stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;
    request();
  },{passive:true});
  update();
})();


;(()=> {
  const agv2AssignmentDeskReady=true;
  const path=window.location.pathname.replace(/\/+$/,'');
  if(!path.startsWith('/blogs/assignment-desk')) return;

  document.body.classList.add('agv2-assignment-desk');
  const root=document.querySelector('#MainContent');
  if(!root) return;

  const reduce=window.matchMedia('(prefers-reduced-motion: reduce)');
  if(reduce.matches) return;
  const fine=window.matchMedia('(hover: hover) and (pointer: fine)');
  const hero=document.querySelector('.assignment-hero');
  const heroImg=hero?.querySelector(':scope > img');
  const heroCopy=hero?.querySelector('.assignment-category-copy');
  const cards=[...root.querySelectorAll('.article-card > .card')];
  const clamp=(v,min,max)=>Math.min(max,Math.max(min,v));
  let stableHeight=document.documentElement.clientHeight||window.innerHeight||800;
  let frame=null;

  function update(){
    frame=null;
    const vh=fine.matches?(window.innerHeight||stableHeight):stableHeight;
    const center=vh*.54;

    if(hero&&heroImg){
      const rect=hero.getBoundingClientRect();
      const p=clamp((vh-rect.top)/(vh+rect.height),0,1);
      heroImg.style.setProperty('--agd-hero-y',(((p-.5)*(fine.matches?11:18))).toFixed(1)+'px');
      heroImg.style.setProperty('--agd-hero-s',(1.018+(1-Math.abs(p-.5)*2)*.014).toFixed(4));
      if(heroCopy){
        heroCopy.style.setProperty('--agd-copy-y',(((.5-p)*(fine.matches?5:10))).toFixed(1)+'px');
      }
    }

    cards.forEach((card,index)=>{
      const rect=card.getBoundingClientRect();
      if(rect.bottom<-180||rect.top>vh+180) return;
      const delta=clamp(((rect.top+rect.height/2)-center)/(vh*(fine.matches?.84:.69)),-1,1);
      const focus=1-Math.abs(delta);
      const direction=index%2===0?-1:1;

      card.style.setProperty('--agd-card-x',(direction*delta*(fine.matches?4:13)).toFixed(1)+'px');
      card.style.setProperty('--agd-card-y',(Math.abs(delta)*(fine.matches?4:10)-focus*(fine.matches?2:3)).toFixed(1)+'px');
      card.style.setProperty('--agd-card-ry',(-direction*delta*(fine.matches?1.2:2.25)).toFixed(2)+'deg');
      card.style.setProperty('--agd-card-s',((fine.matches?.992:.968)+focus*(fine.matches?.016:.040)).toFixed(4));
      card.style.setProperty('--agd-shadow-y',(17+focus*12).toFixed(1)+'px');
      card.style.setProperty('--agd-shadow-blur',(41+focus*21).toFixed(1)+'px');
      card.style.setProperty('--agd-shadow-alpha',(.075+focus*.065).toFixed(3));

      const image=card.querySelector('.article-card__image');
      if(image){
        image.style.setProperty('--agd-img-x',(direction*delta*(fine.matches?4:9)).toFixed(1)+'px');
        image.style.setProperty('--agd-img-y',(delta*(fine.matches?7:14)).toFixed(1)+'px');
        image.style.setProperty('--agd-img-r',(direction*delta*(fine.matches?.5:.95)).toFixed(2)+'deg');
        image.style.setProperty('--agd-img-s',(1.035+focus*(fine.matches?.025:.045)).toFixed(4));
      }
    });
  }

  function request(){
    if(frame!==null) return;
    frame=requestAnimationFrame(update);
  }

  if(fine.matches){
    cards.forEach(card=>{
      const image=card.querySelector('.article-card__image');
      card.addEventListener('pointermove',e=>{
        const r=card.getBoundingClientRect();
        const px=(e.clientX-r.left)/r.width;
        const py=(e.clientY-r.top)/r.height;
        card.style.setProperty('--agd-card-ry',((px-.5)*2.8).toFixed(2)+'deg');
        card.style.setProperty('--agd-card-y',((.5-py)*2.8).toFixed(2)+'px');
        card.style.setProperty('--agd-card-s','1.005');
        if(image){
          image.style.setProperty('--agd-img-x',((px-.5)*13).toFixed(1)+'px');
          image.style.setProperty('--agd-img-y',((py-.5)*12).toFixed(1)+'px');
          image.style.setProperty('--agd-img-r',((px-.5)*1.2).toFixed(2)+'deg');
          image.style.setProperty('--agd-img-s','1.065');
        }
      },{passive:true});
      card.addEventListener('pointerleave',request);
    });
  }

  window.addEventListener('scroll',request,{passive:true});
  window.addEventListener('resize',()=>{
    stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;
    request();
  },{passive:true});
  window.addEventListener('orientationchange',()=>{
    stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;
    request();
  },{passive:true});
  update();
})();


;(()=> {
  const agv2ShopSystemReady=true;
  const path=window.location.pathname.replace(/\/+$/,'')||'/';
  const reduce=window.matchMedia('(prefers-reduced-motion: reduce)');
  const fine=window.matchMedia('(hover: hover) and (pointer: fine)');

  const excludedFree=new Set([
    '/collections/free-resources',
    '/collections/free-guides-e-books',
    '/collections/free-webinars-trainings',
    '/collections/free-tools-templates'
  ]);

  if(path==='/collections'){
    document.body.classList.add('agv2-shop-index');
    const cards=[...document.querySelectorAll('.agv2-shop-category')];
    if(reduce.matches||!cards.length) return;

    const clamp=(v,min,max)=>Math.min(max,Math.max(min,v));
    let stableHeight=document.documentElement.clientHeight||window.innerHeight||800;
    let frame=null;

    const update=()=>{
      frame=null;
      const vh=fine.matches?(window.innerHeight||stableHeight):stableHeight;
      const center=vh*.54;
      cards.forEach((card,index)=>{
        const rect=card.getBoundingClientRect();
        if(rect.bottom<-170||rect.top>vh+170) return;
        const delta=clamp(((rect.top+rect.height/2)-center)/(vh*(fine.matches?.84:.68)),-1,1);
        const focus=1-Math.abs(delta);
        const dir=index%2===0?-1:1;
        card.style.setProperty('--ags-card-x',(dir*delta*(fine.matches?4:12)).toFixed(1)+'px');
        card.style.setProperty('--ags-card-y',(Math.abs(delta)*(fine.matches?4:9)-focus*(fine.matches?2:3)).toFixed(1)+'px');
        card.style.setProperty('--ags-card-ry',(-dir*delta*(fine.matches?1.2:2.2)).toFixed(2)+'deg');
        card.style.setProperty('--ags-card-s',((fine.matches?.992:.970)+focus*(fine.matches?.016:.036)).toFixed(4));
        card.style.setProperty('--ags-shadow-y',(17+focus*12).toFixed(1)+'px');
        card.style.setProperty('--ags-shadow-blur',(41+focus*21).toFixed(1)+'px');
        card.style.setProperty('--ags-shadow-alpha',(.075+focus*.065).toFixed(3));
        const image=card.querySelector('.agv2-shop-category__image');
        if(image){
          image.style.setProperty('--ags-img-x',(dir*delta*(fine.matches?5:10)).toFixed(1)+'px');
          image.style.setProperty('--ags-img-y',(delta*(fine.matches?8:15)).toFixed(1)+'px');
          image.style.setProperty('--ags-img-r',(dir*delta*(fine.matches?.55:1.0)).toFixed(2)+'deg');
          image.style.setProperty('--ags-img-s',(1.04+focus*(fine.matches?.03:.05)).toFixed(4));
        }
      });
    };
    const request=()=>{if(frame===null) frame=requestAnimationFrame(update);};

    if(fine.matches){
      cards.forEach(card=>{
        const image=card.querySelector('.agv2-shop-category__image');
        card.addEventListener('pointermove',e=>{
          const r=card.getBoundingClientRect();
          const px=(e.clientX-r.left)/r.width;
          const py=(e.clientY-r.top)/r.height;
          card.style.setProperty('--ags-card-ry',((px-.5)*2.8).toFixed(2)+'deg');
          card.style.setProperty('--ags-card-y',((.5-py)*2.6).toFixed(2)+'px');
          card.style.setProperty('--ags-card-s','1.006');
          if(image){
            image.style.setProperty('--ags-img-x',((px-.5)*15).toFixed(1)+'px');
            image.style.setProperty('--ags-img-y',((py-.5)*14).toFixed(1)+'px');
            image.style.setProperty('--ags-img-r',((px-.5)*1.3).toFixed(2)+'deg');
            image.style.setProperty('--ags-img-s','1.08');
          }
        },{passive:true});
        card.addEventListener('pointerleave',request);
      });
    }
    window.addEventListener('scroll',request,{passive:true});
    window.addEventListener('resize',()=>{stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;request();},{passive:true});
    update();
    return;
  }

  if(!path.startsWith('/collections/')||excludedFree.has(path)) return;
  document.body.classList.add('agv2-shop-collection');

  const cards=[...document.querySelectorAll('#product-grid .card-wrapper')];
  if(reduce.matches||!cards.length) return;

  const clamp=(v,min,max)=>Math.min(max,Math.max(min,v));
  let stableHeight=document.documentElement.clientHeight||window.innerHeight||800;
  let frame=null;

  const update=()=>{
    frame=null;
    const vh=fine.matches?(window.innerHeight||stableHeight):stableHeight;
    const center=vh*.54;
    cards.forEach((card,index)=>{
      const rect=card.getBoundingClientRect();
      if(rect.bottom<-170||rect.top>vh+170) return;
      const delta=clamp(((rect.top+rect.height/2)-center)/(vh*(fine.matches?.84:.69)),-1,1);
      const focus=1-Math.abs(delta);
      const dir=index%2===0?-1:1;
      card.style.setProperty('--agsp-card-x',(dir*delta*(fine.matches?4:11)).toFixed(1)+'px');
      card.style.setProperty('--agsp-card-y',(Math.abs(delta)*(fine.matches?4:9)-focus*(fine.matches?2:3)).toFixed(1)+'px');
      card.style.setProperty('--agsp-card-ry',(-dir*delta*(fine.matches?1.15:2.05)).toFixed(2)+'deg');
      card.style.setProperty('--agsp-card-s',((fine.matches?.993:.972)+focus*(fine.matches?.015:.034)).toFixed(4));
      card.style.setProperty('--agsp-shadow-y',(16+focus*12).toFixed(1)+'px');
      card.style.setProperty('--agsp-shadow-blur',(40+focus*22).toFixed(1)+'px');
      card.style.setProperty('--agsp-shadow-alpha',(.07+focus*.065).toFixed(3));

      const image=card.querySelector('.card--image-animate');
      if(image){
        image.style.setProperty('--agsp-img-x',(dir*delta*(fine.matches?6:12)).toFixed(1)+'px');
        image.style.setProperty('--agsp-img-y',(delta*(fine.matches?9:17)).toFixed(1)+'px');
        image.style.setProperty('--agsp-img-r',(dir*delta*(fine.matches?.7:1.2)).toFixed(2)+'deg');
        image.style.setProperty('--agsp-img-s',(1.04+focus*(fine.matches?.035:.055)).toFixed(4));
      }
    });
  };
  const request=()=>{if(frame===null) frame=requestAnimationFrame(update);};

  if(fine.matches){
    cards.forEach(card=>{
      const image=card.querySelector('.card--image-animate');
      card.addEventListener('pointermove',e=>{
        const r=card.getBoundingClientRect();
        const px=(e.clientX-r.left)/r.width;
        const py=(e.clientY-r.top)/r.height;
        card.style.setProperty('--agsp-card-ry',((px-.5)*3.0).toFixed(2)+'deg');
        card.style.setProperty('--agsp-card-y',((.5-py)*2.8).toFixed(2)+'px');
        card.style.setProperty('--agsp-card-s','1.006');
        if(image){
          image.style.setProperty('--agsp-img-x',((px-.5)*18).toFixed(1)+'px');
          image.style.setProperty('--agsp-img-y',((py-.5)*17).toFixed(1)+'px');
          image.style.setProperty('--agsp-img-r',((px-.5)*1.5).toFixed(2)+'deg');
          image.style.setProperty('--agsp-img-s','1.085');
        }
      },{passive:true});
      card.addEventListener('pointerleave',request);
    });
  }

  window.addEventListener('scroll',request,{passive:true});
  window.addEventListener('resize',()=>{stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;request();},{passive:true});
  window.addEventListener('orientationchange',()=>{stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;request();},{passive:true});
  update();
})();


;(()=> {
  const agv2ProductPageReady=true;
  const path=window.location.pathname.replace(/\/+$/,'');
  if(!path.startsWith('/products/')) return;
  document.body.classList.add('agv2-product-page');

  const reduce=window.matchMedia('(prefers-reduced-motion: reduce)');
  if(reduce.matches) return;
  const fine=window.matchMedia('(hover: hover) and (pointer: fine)');
  const mediaWrap=document.querySelector('.product__media-wrapper');
  const info=document.querySelector('.product__info-container');
  const clamp=(v,min,max)=>Math.min(max,Math.max(min,v));
  let stableHeight=document.documentElement.clientHeight||window.innerHeight||800;
  let frame=null;

  const activeMedia=()=>document.querySelector('.product__media-item.is-active .media')||document.querySelector('.product__media-item .media');

  function decorateRelated(){
    return [...document.querySelectorAll('.product-recommendations .card-wrapper, .recently-viewed-products .card-wrapper')];
  }

  function update(){
    frame=null;
    const vh=fine.matches?(window.innerHeight||stableHeight):stableHeight;
    const center=vh*.54;

    if(mediaWrap){
      const rect=mediaWrap.getBoundingClientRect();
      const delta=clamp(((rect.top+rect.height/2)-center)/(vh*.82),-1,1);
      mediaWrap.style.setProperty('--agpd-media-y',(delta*(fine.matches?5:10)).toFixed(1)+'px');
      const media=activeMedia();
      if(media){
        media.style.setProperty('--agpd-img-y',(delta*(fine.matches?9:16)).toFixed(1)+'px');
        media.style.setProperty('--agpd-img-s',(1.018+(1-Math.abs(delta))*(fine.matches?.018:.03)).toFixed(4));
      }
    }

    if(info){
      const rect=info.getBoundingClientRect();
      const delta=clamp(((rect.top+rect.height/2)-center)/(vh*.95),-1,1);
      info.style.setProperty('--agpd-info-y',(delta*(fine.matches?4:8)).toFixed(1)+'px');
    }

    decorateRelated().forEach((card,index)=>{
      const rect=card.getBoundingClientRect();
      if(rect.bottom<-150||rect.top>vh+150) return;
      const delta=clamp(((rect.top+rect.height/2)-center)/(vh*.75),-1,1);
      const focus=1-Math.abs(delta);
      const dir=index%2===0?-1:1;
      card.style.setProperty('--agpdr-card-y',(Math.abs(delta)*(fine.matches?3:7)-focus*2).toFixed(1)+'px');
      card.style.setProperty('--agpdr-card-ry',(-dir*delta*(fine.matches?1:1.8)).toFixed(2)+'deg');
      card.style.setProperty('--agpdr-card-s',((fine.matches?.994:.977)+focus*(fine.matches?.012:.027)).toFixed(4));
      const img=card.querySelector('.card--image-animate');
      if(img){
        img.style.setProperty('--agpdr-img-x',(dir*delta*(fine.matches?5:9)).toFixed(1)+'px');
        img.style.setProperty('--agpdr-img-y',(delta*(fine.matches?8:13)).toFixed(1)+'px');
        img.style.setProperty('--agpdr-img-s',(1.04+focus*(fine.matches?.03:.045)).toFixed(4));
      }
    });
  }

  function request(){if(frame===null) frame=requestAnimationFrame(update);}

  if(mediaWrap&&fine.matches){
    mediaWrap.addEventListener('pointermove',e=>{
      const r=mediaWrap.getBoundingClientRect();
      const px=(e.clientX-r.left)/r.width;
      const py=(e.clientY-r.top)/r.height;
      const media=activeMedia();
      if(media){
        media.style.setProperty('--agpd-img-x',((px-.5)*18).toFixed(1)+'px');
        media.style.setProperty('--agpd-img-y',((py-.5)*17).toFixed(1)+'px');
        media.style.setProperty('--agpd-img-r',((px-.5)*1.35).toFixed(2)+'deg');
        media.style.setProperty('--agpd-img-s','1.055');
      }
    },{passive:true});
    mediaWrap.addEventListener('pointerleave',request);
  }

  window.addEventListener('scroll',request,{passive:true});
  window.addEventListener('resize',()=>{stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;request();},{passive:true});
  window.addEventListener('orientationchange',()=>{stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;request();},{passive:true});

  const observer=new MutationObserver(request);
  observer.observe(document.body,{subtree:true,childList:true,attributes:true,attributeFilter:['class']});
  update();
})();


;(()=> {
  const agv2ZoomSafeProductParallax=true;
  const path=window.location.pathname.replace(/\/+$/,'');
  if(!path.startsWith('/products/')) return;

  const reduce=window.matchMedia('(prefers-reduced-motion: reduce)');
  if(reduce.matches) return;
  const fine=window.matchMedia('(hover: hover) and (pointer: fine)');
  const clamp=(v,min,max)=>Math.min(max,Math.max(min,v));
  let stableHeight=document.documentElement.clientHeight||window.innerHeight||800;
  let frame=null;

  const activeStage=()=>{
    const active=document.querySelector('.product__media-item.is-active .product__modal-opener > use-animate.media-wrapper');
    return active||document.querySelector('.product__media-item .product__modal-opener > use-animate.media-wrapper');
  };

  function update(){
    frame=null;
    const stage=activeStage();
    if(!stage) return;
    const vh=fine.matches?(window.innerHeight||stableHeight):stableHeight;
    const item=stage.closest('.product__media-item');
    const rect=(item||stage).getBoundingClientRect();
    const center=vh*.52;
    const delta=clamp(((rect.top+rect.height/2)-center)/(vh*(fine.matches?.78:.64)),-1,1);
    const focus=1-Math.abs(delta);

    stage.style.setProperty('--agpd-stage-x',((fine.matches?4:7)*-delta).toFixed(1)+'px');
    stage.style.setProperty('--agpd-stage-y',((fine.matches?14:24)*delta).toFixed(1)+'px');
    stage.style.setProperty('--agpd-stage-r',((fine.matches?.45:.7)*-delta).toFixed(2)+'deg');
    stage.style.setProperty('--agpd-stage-s',((fine.matches?1.018:1.028)+focus*(fine.matches?.024:.038)).toFixed(4));
  }

  function request(){if(frame===null) frame=requestAnimationFrame(update);}

  if(fine.matches){
    const gallery=document.querySelector('.product__media-wrapper');
    gallery?.addEventListener('pointermove',e=>{
      const stage=activeStage();
      if(!stage) return;
      const r=gallery.getBoundingClientRect();
      const px=(e.clientX-r.left)/r.width;
      const py=(e.clientY-r.top)/r.height;
      stage.style.setProperty('--agpd-stage-x',((px-.5)*18).toFixed(1)+'px');
      stage.style.setProperty('--agpd-stage-y',((py-.5)*17).toFixed(1)+'px');
      stage.style.setProperty('--agpd-stage-r',((px-.5)*1.15).toFixed(2)+'deg');
      stage.style.setProperty('--agpd-stage-s','1.065');
    },{passive:true});
    gallery?.addEventListener('pointerleave',request);
  }

  window.addEventListener('scroll',request,{passive:true});
  window.addEventListener('resize',()=>{
    stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;
    request();
  },{passive:true});
  window.addEventListener('orientationchange',()=>{
    stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;
    request();
  },{passive:true});

  new MutationObserver(request).observe(document.body,{
    subtree:true,
    attributes:true,
    attributeFilter:['class']
  });

  update();
})();


;(()=> {
  const agv2RecommendedToolsReady=true;
  const path=window.location.pathname.replace(/\/+$/,'');
  if(path!=='/pages/recommended-tools') return;

  const reduce=window.matchMedia('(prefers-reduced-motion: reduce)');
  if(reduce.matches) return;

  const fine=window.matchMedia('(hover: hover) and (pointer: fine)');
  const cards=[...document.querySelectorAll('.agt-tool')];
  const clamp=(v,min,max)=>Math.min(max,Math.max(min,v));
  let stableHeight=document.documentElement.clientHeight||window.innerHeight||800;
  let frame=null;

  function update(){
    frame=null;
    const vh=fine.matches?(window.innerHeight||stableHeight):stableHeight;
    const center=vh*.54;
    cards.forEach((card,index)=>{
      const rect=card.getBoundingClientRect();
      if(rect.bottom<-160||rect.top>vh+160) return;
      const delta=clamp(((rect.top+rect.height/2)-center)/(vh*(fine.matches?.85:.68)),-1,1);
      const focus=1-Math.abs(delta);
      const dir=index%2===0?-1:1;
      card.style.setProperty('--agt-card-x',(dir*delta*(fine.matches?4:11)).toFixed(1)+'px');
      card.style.setProperty('--agt-card-y',(Math.abs(delta)*(fine.matches?4:9)-focus*2).toFixed(1)+'px');
      card.style.setProperty('--agt-card-ry',(-dir*delta*(fine.matches?1.1:2)).toFixed(2)+'deg');
      card.style.setProperty('--agt-card-s',((fine.matches?.994:.975)+focus*(fine.matches?.012:.028)).toFixed(4));
    });
  }

  function request(){if(frame===null) frame=requestAnimationFrame(update);}
  window.addEventListener('scroll',request,{passive:true});
  window.addEventListener('resize',()=>{stableHeight=document.documentElement.clientHeight||window.innerHeight||stableHeight;request();},{passive:true});
  update();
})();


;(()=> {
  const agv2ProductMotionDriver=true;
  const path=window.location.pathname.replace(/\/+$/,'');
  if(!path.startsWith('/products/')) return;

  const reduce=window.matchMedia('(prefers-reduced-motion: reduce)');
  if(reduce.matches) return;

  const gallery=document.querySelector('.product__media-wrapper');
  const list=gallery?.querySelector('.product__media-list');
  if(!gallery||!list) return;

  document.body.classList.add('agv2-product-motion-driver');

  const clamp=(v,min,max)=>Math.min(max,Math.max(min,v));
  let tiltEnabled=false;
  const tiltAvailable=('DeviceOrientationEvent' in window);
  let baseBeta=null;
  let baseGamma=null;
  let targetTiltX=0;
  let targetTiltY=0;
  let tiltX=0;
  let tiltY=0;
  let lastActive=null;
  let raf=0;
  const start=performance.now();

  const items=()=>[...list.querySelectorAll('.product__media-item')].filter(item=>item.querySelector('.product__modal-opener .product__media img'));

  function activeItem(){
    const activeThumb=gallery.querySelector('.thumbnail-list__item.is-active[data-target]');
    const target=activeThumb?.getAttribute('data-target');
    if(target){
      const byTarget=[...list.querySelectorAll('.product__media-item[data-media-id]')].find(item=>item.getAttribute('data-media-id')===target);
      if(byTarget?.querySelector('.product__modal-opener .product__media img')) return byTarget;
    }

    const classActive=list.querySelector('.product__media-item.is-active');
    if(classActive?.querySelector('.product__modal-opener .product__media img')) return classActive;

    const all=items();
    if(!all.length) return null;
    const viewport=gallery.querySelector('product-gallery')||list.parentElement||gallery;
    const vr=viewport.getBoundingClientRect();
    const viewportCenter=vr.left+(vr.width/2);
    let best=all[0];
    let bestDistance=Infinity;
    all.forEach(item=>{
      const r=item.getBoundingClientRect();
      const distance=Math.abs((r.left+r.width/2)-viewportCenter);
      if(distance<bestDistance){bestDistance=distance;best=item;}
    });
    return best;
  }

  function activeImage(){
    return activeItem()?.querySelector('.product__modal-opener .product__media img')||null;
  }

  function clearInactive(active){
    items().forEach(item=>{
      const img=item.querySelector('.product__modal-opener .product__media img');
      if(img&&img!==active){
        img.style.removeProperty('transform');
        img.style.removeProperty('filter');
      }
    });
  }

  function render(now){
    const img=activeImage();
    if(img){
      if(img!==lastActive){
        clearInactive(img);
        lastActive=img;
      }

      tiltX+=(targetTiltX-tiltX)*0.08;
      tiltY+=(targetTiltY-tiltY)*0.08;
      const t=(now-start)/1000;
      const autoX=Math.sin(t*0.72)*18+Math.sin(t*0.29)*7;
      const autoY=Math.cos(t*0.58)*20+Math.sin(t*0.24)*7;
      const autoR=Math.sin(t*0.42)*0.55;
      const autoScale=1.105+((Math.sin(t*0.48)+1)/2)*0.025;
      const mix=tiltEnabled?0.28:1;
      const x=(autoX*mix)+(tiltX*(tiltEnabled?1:0));
      const y=(autoY*mix)+(tiltY*(tiltEnabled?1:0));
      const r=autoR*mix+(tiltX*(tiltEnabled?0.018:0));
      const scale=autoScale+(tiltEnabled?(Math.min(1,(Math.abs(tiltX)+Math.abs(tiltY))/28)*0.012):0);
      const transformValue='translate3d('+x.toFixed(2)+'px, '+y.toFixed(2)+'px, 0) scale('+scale.toFixed(4)+') rotate('+r.toFixed(3)+'deg)';
      img.style.setProperty('transform',transformValue,'important');
      img.style.setProperty('filter','saturate(1.015) contrast(1.008)','important');
    }
    raf=requestAnimationFrame(render);
  }

  function handleOrientation(event){
    if(!tiltEnabled) return;
    if(typeof event.beta!=='number'||typeof event.gamma!=='number') return;
    if(baseBeta===null){baseBeta=event.beta;baseGamma=event.gamma;}
    const gamma=clamp(event.gamma-baseGamma,-22,22);
    const beta=clamp(event.beta-baseBeta,-22,22);
    targetTiltX=(gamma/22)*26;
    targetTiltY=(beta/22)*26;
    const activeComposite=activeItem()?.querySelector('.product__modal-opener > use-animate.media-wrapper');
    if(activeComposite){
      activeComposite.style.setProperty('--agv2-tilt-x',targetTiltX.toFixed(1)+'px');
      activeComposite.style.setProperty('--agv2-tilt-y',targetTiltY.toFixed(1)+'px');
      activeComposite.style.setProperty('--agv2-tilt-r',(targetTiltX*0.035).toFixed(2)+'deg');
    }
  }

  function addTiltButton(){
    if(!tiltAvailable||gallery.querySelector('.agv2-tilt-control')) return;
    const button=document.createElement('button');
    button.type='button';
    button.className='agv2-tilt-control';
    button.setAttribute('aria-pressed','false');
    button.innerHTML='<span class="agv2-tilt-control__dot" aria-hidden="true"></span><span class="agv2-tilt-control__label">Tilt image</span>';
    gallery.appendChild(button);

    button.addEventListener('click',async()=>{
      if(tiltEnabled){
        tiltEnabled=false;
        document.body.classList.remove('agv2-tilt-live');
        targetTiltX=0;targetTiltY=0;baseBeta=null;baseGamma=null;
        window.removeEventListener('deviceorientation',handleOrientation);
        button.setAttribute('aria-pressed','false');
        button.querySelector('.agv2-tilt-control__label').textContent='Tilt image';
        return;
      }

      let granted=true;
      try{
        if(typeof DeviceOrientationEvent!=='undefined'&&typeof DeviceOrientationEvent.requestPermission==='function'){
          granted=(await DeviceOrientationEvent.requestPermission())==='granted';
        }
      }catch(e){granted=false;}

      if(!granted){
        button.classList.add('is-unavailable');
        button.querySelector('.agv2-tilt-control__label').textContent='Auto motion on';
        return;
      }

      tiltEnabled=true;
      document.body.classList.add('agv2-tilt-live');
      baseBeta=null;baseGamma=null;
      window.addEventListener('deviceorientation',handleOrientation,{passive:true});
      button.setAttribute('aria-pressed','true');
      button.classList.remove('is-unavailable');
      button.querySelector('.agv2-tilt-control__label').textContent='Tilt on';
    });
  }

  list.addEventListener('click',()=>{
    lastActive=null;
    setTimeout(()=>{clearInactive(activeImage());},80);
  },{passive:true});

  list.addEventListener('scroll',()=>{lastActive=null;},{passive:true});

  const observer=new MutationObserver(()=>{lastActive=null;});
  observer.observe(list,{subtree:true,attributes:true,attributeFilter:['class','aria-hidden','aria-current']});

  addTiltButton();
  raf=requestAnimationFrame(render);

  window.addEventListener('pagehide',()=>{
    cancelAnimationFrame(raf);
    window.removeEventListener('deviceorientation',handleOrientation);
    observer.disconnect();
  },{once:true});
})();


;(()=> {
  const agv2ActiveMediaMarker=true;
  const path=window.location.pathname.replace(/\/+$/,'');
  if(!path.startsWith('/products/')) return;
  const gallery=document.querySelector('.product__media-wrapper');
  const list=gallery?.querySelector('.product__media-list');
  if(!gallery||!list) return;

  function resolveActive(){
    const thumb=gallery.querySelector('.thumbnail-list__item.is-active[data-target]');
    const target=thumb?.getAttribute('data-target');
    if(target){
      const match=[...list.querySelectorAll('.product__media-item[data-media-id]')].find(item=>item.getAttribute('data-media-id')===target);
      if(match) return match;
    }
    const byClass=list.querySelector('.product__media-item.is-active');
    if(byClass) return byClass;

    const viewport=gallery.querySelector('product-gallery')||list.parentElement||gallery;
    const vr=viewport.getBoundingClientRect();
    const center=vr.left+(vr.width/2);
    let best=null;
    let distance=Infinity;
    list.querySelectorAll('.product__media-item').forEach(item=>{
      const r=item.getBoundingClientRect();
      const d=Math.abs((r.left+r.width/2)-center);
      if(d<distance){distance=d;best=item;}
    });
    return best;
  }

  function mark(){
    const active=resolveActive();
    list.querySelectorAll('.product__media-item').forEach(item=>{
      if(item===active) item.setAttribute('data-agv2-active-media','true');
      else item.removeAttribute('data-agv2-active-media');
    });
  }

  mark();
  list.addEventListener('click',()=>setTimeout(mark,60),{passive:true});
  list.addEventListener('scroll',mark,{passive:true});
  gallery.querySelector('.thumbnail-slider')?.addEventListener('click',()=>setTimeout(mark,60),{passive:true});
  new MutationObserver(mark).observe(gallery,{subtree:true,attributes:true,attributeFilter:['class','aria-current']});
})();


;(()=> {
  const agv2AssignmentBeatFocus=true;
  const path=window.location.pathname.replace(/\/+$/,'');
  if(!path.startsWith('/blogs/assignment-desk')) return;

  const beats=document.querySelector('.assignment-beats');
  const active=beats?.querySelector('a.is-active');
  if(!beats||!active) return;

  active.setAttribute('aria-current','page');

  const reduce=window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const centerActive=()=>{
    const max=Math.max(0,beats.scrollWidth-beats.clientWidth);
    const desired=active.offsetLeft-((beats.clientWidth-active.offsetWidth)/2);
    beats.scrollTo({
      left:Math.max(0,Math.min(max,desired)),
      behavior:reduce?'auto':'smooth'
    });
  };

  requestAnimationFrame(()=>requestAnimationFrame(centerActive));
})();

/* AG V2 Privacy Choice Control — customer-initiated Shopify data-sale/sharing opt-out */
(()=>{
  const initAgv2PrivacyChoice=()=>{
    if(!document.body.classList.contains('page-ccpa-opt-out')) return;
    const root=document.querySelector('#MainContent .rte');
    if(!root || document.getElementById('agv2-privacy-choice')) return;

    const card=document.createElement('section');
    card.id='agv2-privacy-choice';
    card.className='agv2-privacy-choice';
    card.setAttribute('aria-labelledby','agv2-privacy-choice-title');
    card.innerHTML=`
      <p class="agv2-privacy-choice__kicker">YOUR PRIVACY CHOICES</p>
      <h2 id="agv2-privacy-choice-title">Opt out of data sale or sharing</h2>
      <p>Use the button below to record a data sale/sharing opt-out for this browser or device where that choice applies. Shopify also supports qualifying Global Privacy Control (GPC) signals where applicable.</p>
      <div class="agv2-privacy-choice__actions">
        <button type="button" class="button agv2-privacy-choice__button" id="agv2-data-sale-optout">Opt out of data sale/sharing</button>
        <a class="agv2-privacy-choice__email" href="mailto:iamaliciagtv@gmail.com?subject=Privacy%20Request">Email a privacy request</a>
      </div>
      <p class="agv2-privacy-choice__status" id="agv2-privacy-choice-status" role="status" aria-live="polite"></p>
    `;
    root.appendChild(card);

    const button=card.querySelector('#agv2-data-sale-optout');
    const status=card.querySelector('#agv2-privacy-choice-status');
    const getApi=()=>window.Shopify && window.Shopify.customerPrivacy ? window.Shopify.customerPrivacy : null;

    const reflectCurrentState=()=>{
      const api=getApi();
      if(api && typeof api.saleOfDataAllowed==='function'){
        try{
          if(api.saleOfDataAllowed()===false){
            button.disabled=true;
            button.textContent='Opt-out preference is active';
            status.textContent='Data sale/sharing is currently blocked for this browser or device.';
          }
        }catch(e){}
      }
    };

    button.addEventListener('click',()=>{
      const api=getApi();
      if(!api || typeof api.setTrackingConsent!=='function'){
        status.textContent='We could not save the browser preference automatically. Please use the email option below or enable Global Privacy Control (GPC) in your browser.';
        return;
      }
      button.disabled=true;
      button.textContent='Saving your preference…';
      try{
        api.setTrackingConsent({sale_of_data:false},()=>{
          button.textContent='Opt-out preference saved';
          status.textContent='Your data sale/sharing opt-out preference has been recorded for this browser or device.';
        });
      }catch(e){
        button.disabled=false;
        button.textContent='Opt out of data sale/sharing';
        status.textContent='We could not save the browser preference automatically. Please use the email option below or enable Global Privacy Control (GPC) in your browser.';
      }
    });

    document.addEventListener('visitorConsentCollected',reflectCurrentState);
    reflectCurrentState();
  };

  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',initAgv2PrivacyChoice,{once:true});
  else initAgv2PrivacyChoice();
})();


/* AG V2 PUBLISHED WORK MOTION BOOST:1 */
(() => {
  const initPublishedWorkMotion = () => {
    if (!document.body.classList.contains('page-alicia-gonzalez')) return;
    const root = document.querySelector('#published-work');
    if (!root) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce.matches) return;

    const mobile = window.matchMedia('(max-width: 900px)');
    const cards = Array.from(root.querySelectorAll('.agv2-pw-card'));
    const story = root.querySelector('.agv2-pw-story');
    const linkedin = root.querySelector('.agv2-pw-linkedin');
    const movers = [...cards, story, linkedin].filter(Boolean);
    if (!movers.length) return;

    let frame = null;
    const clamp = (v,min,max) => Math.min(max,Math.max(min,v));

    const update = () => {
      frame = null;
      if (!mobile.matches || reduce.matches) {
        movers.forEach(el => {
          el.style.removeProperty('--agv2-pw-scroll-y');
          el.style.removeProperty('--agv2-pw-scroll-scale');
        });
        return;
      }

      const vh = document.documentElement.clientHeight || window.innerHeight || 800;
      const target = vh * .54;

      movers.forEach((el,index) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -180 || rect.top > vh + 180) return;
        const center = rect.top + rect.height / 2;
        const delta = clamp((center - target) / (vh * .72), -1, 1);
        const focus = 1 - Math.abs(delta);
        const y = -delta * (el.classList.contains('agv2-pw-card') ? 12 : 8);
        const scale = .986 + (focus * .014);
        el.style.setProperty('--agv2-pw-scroll-y', y.toFixed(1) + 'px');
        el.style.setProperty('--agv2-pw-scroll-scale', scale.toFixed(4));
        if (el.classList.contains('agv2-pw-card')) {
          el.dataset.agv2PwFocus = focus > .68 ? 'true' : 'false';
        }
      });
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll',requestUpdate,{passive:true});
    window.addEventListener('resize',requestUpdate,{passive:true});
    mobile.addEventListener?.('change',requestUpdate);
    reduce.addEventListener?.('change',requestUpdate);
    update();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded',initPublishedWorkMotion,{once:true});
  } else {
    initPublishedWorkMotion();
  }
})();


/* AG V2 NATIVE POLICY STRUCTURE:1 */
(() => {
  const isHeadingText = (text) => {
    const value = (text || '').replace(/\s+/g, ' ').trim();
    if (!value || value.length < 3 || value.length > 82) return false;
    if (/^last updated\s*:/i.test(value)) return false;
    if (/^[-•]/.test(value)) return false;
    if (/[.!?;:]$/.test(value)) return false;
    const words = value.split(/\s+/);
    return words.length <= 12;
  };

  const makeHeading = (node, text) => {
    const heading = document.createElement('h2');
    heading.textContent = text.trim();
    node.replaceWith(heading);
  };

  const structurePolicy = () => {
    const rte = document.querySelector('.shopify-policy__body .rte');
    if (!rte || rte.dataset.agv2Structured === 'true') return;

    const meaningfulChildren = Array.from(rte.children).filter((el) => {
      const text = (el.textContent || '').replace(/\u00a0/g, ' ').trim();
      return text || !el.matches('p');
    });

    if (meaningfulChildren.length < 3 && rte.textContent.includes('\n')) {
      const blocks = rte.textContent
        .split(/\n\s*\n+/)
        .map((part) => part.replace(/\s+/g, ' ').trim())
        .filter(Boolean);

      if (blocks.length >= 3) {
        const fragment = document.createDocumentFragment();
        blocks.forEach((text) => {
          const el = document.createElement(isHeadingText(text) ? 'h2' : 'p');
          el.textContent = text;
          fragment.appendChild(el);
        });
        rte.replaceChildren(fragment);
      }
    } else {
      Array.from(rte.children).forEach((el) => {
        if (!el.matches('p')) return;
        const text = (el.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
        if (!text && (el.querySelector('br') || el.innerHTML.trim() === '')) {
          el.remove();
          return;
        }
        if (isHeadingText(text)) makeHeading(el, text);
      });
    }

    rte.dataset.agv2Structured = 'true';
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', structurePolicy, { once: true });
  } else {
    structurePolicy();
  }
})();
